// ─────────────────────────────────────────────────────────────
// Forecast Context Builder
// Builds the deep, layered context object sent to the forecast
// edge function so Claude can generate precise, empathetic
// forecasts based on real planetary transits.
// ─────────────────────────────────────────────────────────────

import { PLANET_NAMES_PT, PLANET_GLYPHS, longitudeToSign, calculatePlanetPositions } from './ephemeris';
import type { Planet } from './ephemeris';
import type { NatalChartData, TransitAnalysisResult } from './transitAnalysis';
import { analyzeTransits, getTransitTheme, formatTransitLabel } from './transitAnalysis';

// ─── Types ──────────────────────────────────────────────────

export type ForecastPeriod = 'daily' | 'weekly' | 'monthly' | 'annual';

// ─── Period-relevant planets ─────────────────────────────────
// Each period uses only the planets whose speed matches the time scale.
// Transit analysis is always transit × natal (not generic sun-sign).

const PERIOD_PLANETS: Record<ForecastPeriod, Planet[]> = {
  daily:   ['Moon', 'Sun', 'Mercury', 'Venus'],
  weekly:  ['Moon', 'Sun', 'Mercury', 'Venus'],
  monthly: ['Sun', 'Venus', 'Mars'],
  annual:  ['Saturn', 'Uranus', 'Neptune', 'Pluto'],
};

export interface MediumTermConfig {
  description: string;
  daysActive: number;
  daysRemaining: number;
  intensity: number;
  theme: string;
  lifeAreas: string[];
  phaseNarrative: string;
  accumulatedImpact: string;
  upcomingImpact: string;
}

export interface LongTermConfig {
  description: string;
  monthsActive: number;
  monthsRemaining: number;
  intensity: number;
  theme: string;
  lifeAreas: string[];
  transformativeNarrative: string;
  previousPassages: string;
}

export interface DeepForecastContext {
  identity: {
    name: string;
    sunSign: string;
    moonSign: string;
    natalSummary: string;
    natalPlanets: string;
    dominantElement: string;
    dominantModality: string;
  };
  skyToday: {
    date: string;
    positions: string;
    retrogradesPt: string;
    eclipsesPt: string;
    moonSign: string;
    moonPhase: string;
    sunSign: string;
  };
  shortTermTransits: {
    summary: string;
    topTransits: string;
  };
  mediumTermTransits: {
    summary: string;
    activeConfigurations: MediumTermConfig[];
  };
  longTermTransits: {
    summary: string;
    backgroundConfigurations: LongTermConfig[];
    cumulativeNarrative: string;
  };
  integratedSynthesis: string;
  period: ForecastPeriod;
  periodDates: string;
  /** Pre-filtered transits for the period's relevant planets with exact degrees, houses, aspects, orbs. */
  periodFocusTransits: string;
  /** Per-day Moon+Sun+Mercury+Venus positions (weekly only, otherwise empty string) */
  weeklyDaySky: string;
}

// ─── Period date range helpers ──────────────────────────────

function getPeriodDates(period: ForecastPeriod, date: Date): { start: Date; end: Date } {
  const d = new Date(date);
  switch (period) {
    case 'daily':
      return { start: d, end: d };
    case 'weekly': {
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday start
      const start = new Date(d); start.setDate(diff);
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return { start, end };
    }
    case 'monthly': {
      const start = new Date(d.getFullYear(), d.getMonth(), 1);
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      return { start, end };
    }
    case 'annual': {
      const start = new Date(d.getFullYear(), 0, 1);
      const end = new Date(d.getFullYear(), 11, 31);
      return { start, end };
    }
  }
}

// ─── Extract natal sign names ───────────────────────────────

function getNatalSign(planets: NatalChartData['planets'], key: string): string {
  return planets[key]?.sign ?? 'desconhecido';
}

// ─── Period-focus transit block ─────────────────────────────
// Filters raw transit data to only the planets relevant for the period,
// then formats each as: planet + position + aspect + natal point + house + orb.
// This gives Claude precise, personalized data instead of generic text.

function buildPeriodFocusBlock(
  analysis: TransitAnalysisResult,
  period: ForecastPeriod,
  name: string,
): string {
  const relevant = PERIOD_PLANETS[period];

  // Current positions of period-relevant planets
  const positions = analysis.currentSky
    .filter(p => relevant.includes(p.planet))
    .map(p => {
      const rx    = p.isRetrograde ? ' ℞' : '';
      const house = p.house > 0 ? ` (Casa ${p.house} natal)` : '';
      return `${PLANET_GLYPHS[p.planet]} ${PLANET_NAMES_PT[p.planet]}: ${p.degreeFormatted}${house}${rx}`;
    })
    .join('\n');

  // Active transits for period-relevant planets, best first
  const relevantTransits = analysis.allTransits
    .filter(t => relevant.includes(t.transitPlanet))
    .sort((a, b) => b.intensity - a.intensity)
    .slice(0, 15);

  if (relevantTransits.length === 0) {
    return `POSIÇÕES ATUAIS:\n${positions}\n\nNenhum aspecto ativo significativo entre os planetas relevantes e o mapa natal de ${name} neste período.`;
  }

  const transitsStr = relevantTransits.map(t => {
    const rx       = t.transitIsRetrograde ? ' ℞' : '';
    const pos      = longitudeToSign(t.transitLongitude);
    const houseStr = t.natalHouse > 0 ? `, ativa Casa ${t.natalHouse} natal` : '';
    const exact    = t.window.exactDate.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' });
    const remain   = t.window.daysRemaining > 0
      ? `encerra em ${t.window.daysRemaining} dias`
      : 'encerrando hoje';
    return (
      `${PLANET_GLYPHS[t.transitPlanet]} ${PLANET_NAMES_PT[t.transitPlanet]}${rx} em ${pos.degreeFormatted} ` +
      `${t.aspect.symbol} ${t.aspect.namePt} ${t.natalPoint} natal${houseStr}\n` +
      `  Orbe: ${t.currentOrb.toFixed(1)}° | Intensidade: ${t.intensity}% | exato em ${exact} | ${remain}`
    );
  }).join('\n\n');

  return `POSIÇÕES ATUAIS:\n${positions}\n\nASPECTOS ATIVOS COM O MAPA NATAL DE ${name.toUpperCase()}:\n${transitsStr}`;
}

// ─── Per-day sky for weekly forecasts ──────────────────────
// Computes Moon + Sun + Mercury + Venus positions for each day
// of the week so Claude can reference them correctly per-day.

const WEEKDAYS_PT = ['Domingo','Segunda-feira','Terça-feira','Quarta-feira','Quinta-feira','Sexta-feira','Sábado'];

function buildWeeklyDaySkyBlock(weekStart: Date): string {
  const lines: string[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(weekStart);
    day.setDate(weekStart.getDate() + i);
    const positions = calculatePlanetPositions(day);
    const moon    = positions.find(p => p.planet === 'Moon');
    const sun     = positions.find(p => p.planet === 'Sun');
    const mercury = positions.find(p => p.planet === 'Mercury');
    const venus   = positions.find(p => p.planet === 'Venus');
    const date    = day.toLocaleDateString('pt-BR', { day: 'numeric', month: 'numeric' });
    const weekday = WEEKDAYS_PT[day.getDay()];
    const moonRx  = moon?.isRetrograde ? ' ℞' : '';
    const parts   = [
      moon    ? `Lua em ${moon.sign}${moonRx}`           : '',
      sun     ? `Sol em ${sun.sign}`                     : '',
      mercury ? `Mercúrio em ${mercury.sign}${mercury.isRetrograde ? ' ℞' : ''}` : '',
      venus   ? `Vênus em ${venus.sign}${venus.isRetrograde ? ' ℞' : ''}`        : '',
    ].filter(Boolean).join(' · ');
    lines.push(`${weekday} ${date}: ${parts}`);
  }
  return lines.join('\n');
}

// ─── Build the deep context ─────────────────────────────────

export function buildDeepForecastContext(
  natalChart: NatalChartData,
  userName: string,
  period: ForecastPeriod,
  date: Date = new Date(),
): DeepForecastContext {
  const analysis = analyzeTransits(natalChart, date);

  const sunSign = getNatalSign(natalChart.planets, 'sun');
  const moonSign = getNatalSign(natalChart.planets, 'moon');

  // Build natal planets summary (include ascendant when available)
  const PLANET_KEY_TO_PT: Record<string, string> = {
    sun: 'Sol', moon: 'Lua', mercury: 'Mercúrio', venus: 'Vênus', mars: 'Marte',
    jupiter: 'Júpiter', saturn: 'Saturno', uranus: 'Urano', neptune: 'Netuno', pluto: 'Plutão',
  };
  const natalPlanetsStr = Object.entries(natalChart.planets)
    .filter(([key]) => key !== 'ascendant' && key !== 'mc')
    .map(([key, p]) => {
      const name = PLANET_KEY_TO_PT[key] ?? key;
      const retro = p.retrograde ? ' (Rx)' : '';
      return `${name} em ${p.sign} ${p.degree.toFixed(1)}° Casa ${p.house}${retro}`;
    })
    .join(' · ');

  const ascSign = natalChart.planets.ascendant?.sign ?? null;
  const mcSign  = natalChart.planets.mc?.sign ?? null;

  // Current sky summary (deduplicated)
  const skyPositions = analysis.currentSky
    .map(p => `${PLANET_NAMES_PT[p.planet]}: ${p.degreeFormatted}`)
    .join(' · ');

  const retrogrades = analysis.retrogradePlanets
    .map(p => PLANET_NAMES_PT[p])
    .join(', ') || 'Nenhum planeta em retrógrado';

  // Current moon sign
  const currentMoonPos = analysis.currentSky.find(p => p.planet === 'Moon');
  const currentMoonSign = currentMoonPos?.sign ?? '';
  const currentSunPos = analysis.currentSky.find(p => p.planet === 'Sun');
  const currentSunSign = currentSunPos?.sign ?? '';

  // Medium-term configurations
  const mediumConfigs: MediumTermConfig[] = analysis.mediumTerm.slice(0, 10).map(t => {
    const themeData = getTransitTheme(t.transitPlanet, t.aspect.namePt, t.natalPoint);
    return {
      description: `${formatTransitLabel(t)}, ativo há ${t.window.daysActive} dias`,
      daysActive: t.window.daysActive,
      daysRemaining: t.window.daysRemaining,
      intensity: t.intensity,
      theme: themeData.theme,
      lifeAreas: themeData.areas,
      phaseNarrative: t.window.phaseNarrativePt,
      accumulatedImpact: `Este trânsito está ativo desde ${t.window.startDate.toLocaleDateString('pt-BR')} e tem trabalhado as áreas de: ${themeData.areas.join(', ')}.`,
      upcomingImpact: t.window.daysRemaining > 7
        ? `Ainda ficará ativo por ~${t.window.daysRemaining} dias, com intensidade ${t.intensity >= 70 ? 'elevada' : t.intensity >= 40 ? 'moderada' : 'decrescente'}.`
        : `Está encerrando em ${t.window.daysRemaining} dias, fase de integração.`,
    };
  });

  // Long-term configurations
  const longConfigs: LongTermConfig[] = analysis.longTerm.slice(0, 8).map(t => {
    const themeData = getTransitTheme(t.transitPlanet, t.aspect.namePt, t.natalPoint);
    const monthsActive = Math.round(t.window.daysActive / 30);
    const monthsRemaining = Math.round(t.window.daysRemaining / 30);
    return {
      description: `${formatTransitLabel(t)}, ativo há ${monthsActive} ${monthsActive === 1 ? 'mês' : 'meses'}`,
      monthsActive,
      monthsRemaining,
      intensity: t.intensity,
      theme: themeData.theme,
      lifeAreas: themeData.areas,
      transformativeNarrative: `Configuração de longo prazo moldando as áreas de ${themeData.areas.join(', ')} desde ${t.window.startDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}. Permanecerá ativa por mais ${monthsRemaining} ${monthsRemaining === 1 ? 'mês' : 'meses'}.`,
      previousPassages: t.transitIsRetrograde
        ? 'Planeta retrógrado, esta pode ser a 2ª ou 3ª passagem, intensificando o trabalho interno.'
        : '',
    };
  });

  const cumulativeNarrative = analysis.longTerm.length > 0
    ? `${userName} está sob influência de ${analysis.longTerm.length} configuração(ões) de longo prazo. ${longConfigs.map(c => c.transformativeNarrative).join(' ')}`
    : 'Não há trânsitos de longo prazo dominantes, período de relativa liberdade das grandes pressões planetárias.';

  // Integrated synthesis
  const integratedSynthesis = buildIntegratedSynthesis(analysis, userName);

  // Upcoming eclipses within ±60 days (approximate known dates 2025-2026)
  const KNOWN_ECLIPSES: { date: string; type: string; label: string }[] = [
    { date: '2025-03-14', type: 'lunar',  label: 'Eclipse Lunar Total (14 Mar 2025)' },
    { date: '2025-03-29', type: 'solar',  label: 'Eclipse Solar Parcial (29 Mar 2025)' },
    { date: '2025-09-07', type: 'lunar',  label: 'Eclipse Lunar Total (7 Set 2025)' },
    { date: '2025-09-21', type: 'solar',  label: 'Eclipse Solar Anular (21 Set 2025)' },
    { date: '2026-03-03', type: 'lunar',  label: 'Eclipse Lunar Total (3 Mar 2026)' },
    { date: '2026-03-20', type: 'solar',  label: 'Eclipse Solar Total (20 Mar 2026)' },
    { date: '2026-08-28', type: 'lunar',  label: 'Eclipse Lunar Parcial (28 Ago 2026)' },
    { date: '2026-09-12', type: 'solar',  label: 'Eclipse Solar Anular (12 Set 2026)' },
  ];
  const eclipseContext = KNOWN_ECLIPSES
    .filter(e => {
      const diff = (new Date(e.date).getTime() - date.getTime()) / 86_400_000;
      return diff >= -14 && diff <= 60; // within last 14 days or next 60
    })
    .map(e => e.label);

  // Period dates
  const { start, end } = getPeriodDates(period, date);
  const periodDates = period === 'daily'
    ? start.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : `${start.toLocaleDateString('pt-BR')} a ${end.toLocaleDateString('pt-BR')}`;

  // Short term transits summary
  const topShort = analysis.shortTerm.slice(0, 8).map(t =>
    `${formatTransitLabel(t)} | ${t.window.durationSummaryPt} | Intensidade: ${t.intensity}% | ${t.window.phaseNarrativePt}`,
  ).join('\n');

  // Period-specific planet focus (transit × natal, filtered by relevant planets)
  const periodFocusTransits = buildPeriodFocusBlock(analysis, period, userName);

  // For weekly forecasts: per-day Moon+fast planet positions
  const weeklyDaySky = period === 'weekly' ? buildWeeklyDaySkyBlock(start) : '';

  const ascLabel = ascSign ? `, Ascendente em ${ascSign}` : '';
  const mcLabel  = mcSign  ? `, MC em ${mcSign}` : '';

  return {
    identity: {
      name: userName,
      sunSign,
      moonSign,
      natalSummary: `${sunSign} com Lua em ${moonSign}${ascLabel}${mcLabel}`,
      natalPlanets: natalPlanetsStr + (ascSign ? ` · Ascendente em ${ascSign}` : '') + (mcSign ? ` · MC em ${mcSign}` : ''),
      dominantElement: natalChart.dominant_element ?? '',
      dominantModality: natalChart.dominant_modality ?? '',
    },
    skyToday: {
      date: date.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
      positions: skyPositions,
      retrogradesPt: retrogrades,
      eclipsesPt: eclipseContext.length > 0
        ? eclipseContext.join('; ')
        : 'Nenhum eclipse próximo nos próximos 60 dias',
      moonSign: currentMoonSign,
      moonPhase: analysis.moonPhase.namePt,
      sunSign: currentSunSign,
    },
    shortTermTransits: {
      summary: `${analysis.shortTerm.length} trânsito(s) de curto prazo ativo(s)`,
      topTransits: topShort || 'Nenhum trânsito de curto prazo significativo.',
    },
    mediumTermTransits: {
      summary: `${analysis.mediumTerm.length} configuração(ões) em vigor há mais de 30 dias`,
      activeConfigurations: mediumConfigs,
    },
    longTermTransits: {
      summary: `${analysis.longTerm.length} configuração(ões) de fundo de longo prazo`,
      backgroundConfigurations: longConfigs,
      cumulativeNarrative,
    },
    integratedSynthesis,
    period,
    periodDates,
    periodFocusTransits,
    weeklyDaySky,
  };
}

// ─── Integrated synthesis ───────────────────────────────────

function buildIntegratedSynthesis(
  analysis: TransitAnalysisResult,
  name: string,
): string {
  const parts: string[] = [];

  if (analysis.longTerm.length > 0) {
    parts.push(`${name} está em um período de transformação de fundo, os planetas lentos estão trabalhando temas profundos ao longo dos últimos meses.`);
  }

  if (analysis.mediumTerm.length > 0) {
    const challenging = analysis.mediumTerm.filter(t => t.aspect.nature === 'challenging').length;
    const harmonious = analysis.mediumTerm.filter(t => t.aspect.nature === 'harmonious').length;
    if (challenging > harmonious) {
      parts.push('As configurações de médio prazo trazem principalmente desafios que exigem adaptação e crescimento.');
    } else if (harmonious > challenging) {
      parts.push('As configurações de médio prazo oferecem um fluxo favorável que pode ser aproveitado intencionalmente.');
    } else {
      parts.push('As configurações de médio prazo trazem uma mistura de desafios e oportunidades.');
    }
  }

  if (analysis.shortTerm.length > 0) {
    parts.push('Os trânsitos de curto prazo modulam a experiência do dia a dia dentro desse contexto maior.');
  }

  if (analysis.retrogradePlanets.length > 0) {
    const names = analysis.retrogradePlanets.map(p => PLANET_NAMES_PT[p]).join(', ');
    parts.push(`Retrógrados ativos (${names}) pedem revisão interna e cautela em decisões precipitadas.`);
  }

  return parts.join(' ') || 'Período de relativa estabilidade, sem grandes pressões planetárias.';
}

// ─── Serialize context to prompt string ─────────────────────

export function serializeContextForPrompt(ctx: DeepForecastContext): string {
  let s = '';

  s += `═══ MAPA NATAL DE ${ctx.identity.name.toUpperCase()} ═══\n`;
  s += `${ctx.identity.natalSummary}\n`;
  s += `Posições natais: ${ctx.identity.natalPlanets}\n`;
  if (ctx.identity.dominantElement) s += `Elemento dominante: ${ctx.identity.dominantElement}\n`;
  if (ctx.identity.dominantModality) s += `Modalidade dominante: ${ctx.identity.dominantModality}\n`;

  s += `\n═══ CÉU AGORA ═══\n`;
  s += `📅 Data da consulta: ${ctx.skyToday.date}\n`;
  s += `Período analisado: ${ctx.periodDates}\n`;
  s += `${ctx.skyToday.positions}\n`;
  s += `Lua em: ${ctx.skyToday.moonSign} | ${ctx.skyToday.moonPhase}\n`;
  s += `Retrógrados: ${ctx.skyToday.retrogradesPt}\n`;
  s += `Eclipses próximos: ${ctx.skyToday.eclipsesPt}\n`;

  // ─── Period-focus block (most important section) ────────────
  s += `\n═══ PLANETAS DO PERÍODO, TRÂNSITO × MAPA NATAL ═══\n`;
  s += `(Análise personalizada: posição atual de cada planeta vs. pontos natais de ${ctx.identity.name})\n`;
  s += ctx.periodFocusTransits;
  s += '\n';

  if (ctx.weeklyDaySky) {
    s += `\n═══ POSIÇÃO DA LUA E PLANETAS RÁPIDOS, DIA A DIA ═══\n`;
    s += `(Use estas posições para mencionar a Lua corretamente em cada dia do Resumo Diário)\n`;
    s += ctx.weeklyDaySky;
    s += '\n';
  }

  s += `\n═══ CAMADA 1, TRÂNSITOS DE CURTO PRAZO ═══\n`;
  s += ctx.shortTermTransits.topTransits || 'Nenhum.';
  s += '\n';

  if (ctx.mediumTermTransits.activeConfigurations.length > 0) {
    s += `\n═══ CAMADA 2, CONFIGURAÇÕES EM VIGOR HÁ MAIS DE 30 DIAS ═══\n`;
    s += `(Estas já estão moldando a vida de ${ctx.identity.name} há semanas, leve isso em conta)\n\n`;
    for (const c of ctx.mediumTermTransits.activeConfigurations) {
      s += `• ${c.description}\n`;
      s += `  Tema: ${c.theme}\n`;
      s += `  Áreas afetadas: ${c.lifeAreas.join(', ')}\n`;
      s += `  Impacto acumulado: ${c.accumulatedImpact}\n`;
      s += `  Fase atual: ${c.phaseNarrative}\n`;
      s += `  O que ainda vem: ${c.upcomingImpact}\n\n`;
    }
  }

  if (ctx.longTermTransits.backgroundConfigurations.length > 0) {
    s += `\n═══ CAMADA 3, TRANSFORMAÇÕES DE LONGO PRAZO ═══\n`;
    s += `${ctx.longTermTransits.cumulativeNarrative}\n\n`;
    for (const c of ctx.longTermTransits.backgroundConfigurations) {
      s += `• ${c.description}\n`;
      s += `  Tema: ${c.theme}\n`;
      s += `  Narrativa: ${c.transformativeNarrative}\n`;
      if (c.previousPassages) s += `  ${c.previousPassages}\n`;
      s += '\n';
    }
  }

  s += `\n═══ SÍNTESE INTEGRADORA ═══\n`;
  s += ctx.integratedSynthesis;

  return s;
}
